import { useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  User,
  UserPlus,
  Check,
  X,
  Plus,
  FileText,
  FileSpreadsheet,
  Package,
  Zap,
  Clock,
  Edit,
  Trash2,
  ArrowRight,
  ExternalLink,
  Truck,
  Sparkles,
  CheckCircle2,
  FlaskConical,
  XCircle,
  Ban,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { AdditionalInfoDisplay } from '@/shared/components/AdditionalInfoFields';
import { useUser } from '@/shared/hooks/useAuth';
import { useHasAnyPermission } from '@/shared/permissions/useHasPermission';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { usePipelineRecord, usePipelineStages, usePipelineMutations, stageErrorText } from '../hooks/usePipeline';
import { userName, leadNumberLabel } from '../types';
import type { PipelineRecord, PipelineLine } from '../types';
import { ProductLinesEditor } from '../components/ProductLinesEditor';
import {
  CustomerPoFields,
  customerPoFromRecord,
  isCustomerPoIncomplete,
  type CustomerPoValue,
} from '../components/CustomerPoFields';
import { StageRegressionNoteModal } from '../components/StageRegressionNoteModal';
import { LeadSamplingPanel } from '@/features/leads/components/LeadSamplingPanel';
// Deal-shaped pieces this record still shares with the legacy deals feature —
// the assignment hand-off flow and the AI win-score have no pipeline-native
// equivalent yet, and LostReasonModal already reads exactly the fields a
// PipelineRecord carries, so it is reused as-is rather than duplicated.
import { useDealMutations, useDealScores } from '@/features/deals/hooks/useDeals';
import { LostReasonModal } from '@/features/deals/components/LostReasonModal';
import type { Deal, AssignmentStatus } from '@/features/deals/types';
import { getAssignedId } from '@/features/deals/types';
import { formatMoney, DEAL_SOURCE_LABELS } from '@/features/deals/constants';
import { useDealStages } from '@/features/deals/hooks/useDealStages';
import { ActivityTimeline } from '@/features/activity/components/ActivityTimeline';
import { AppointmentPanel, FollowupPanel } from '@/features/activity/components/TimelinePanel';
import { useEntityActivity } from '@/features/activity/hooks/useActivity';
import { useSalesDocs, useSalesDocMutations } from '@/features/salesdocs/hooks/useSalesDocs';
import { SalesDocFormModal } from '@/features/salesdocs/components/SalesDocFormModal';
import { ROUTES } from '@/routes/paths';
import { SharedRecordBanner } from '../components/SharedRecordBanner';
import { CustomerHistoryPanel } from '../components/CustomerHistoryPanel';
import { CompleteLeadDialog } from '../components/CompleteLeadDialog';
import { leadWorkGaps } from '../lead-work';
import { useTimelineList } from '@/features/activity/hooks/useFollowups';

const ASSIGNMENT_META: Record<Exclude<AssignmentStatus, 'none'>, { label: string; tone: 'warning' | 'success' | 'danger' }> = {
  pending: { label: 'Pending Acceptance', tone: 'warning' },
  accepted: { label: 'Accepted', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
};

/** Cast to Deal for the two legacy modals — every field they read exists,
 * identically named, on PipelineRecord (both descend from the same schema). */
const asDeal = (r: PipelineRecord) => r as unknown as Deal;

// Same admin-tier set the backend's PIPELINE_FULL_ACCESS_ROLES (and
// SalesPipelinePage's own PIPELINE_ADMIN_ROLES) already use — approving a
// reassignment request is one of the few actions genuinely reserved to it.
const PIPELINE_ADMIN_ROLES = new Set(['superadmin', 'admin', 'ceo', 'owner', 'department_admin']);

export function PipelineRecordDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const timelineParentParam = searchParams.get('timelineParent');
  const timelineParent = timelineParentParam === 'lead' || timelineParentParam === 'enquiry' ? timelineParentParam : 'deal';
  const user = useUser();
  const currentUserId = user?.id ?? '';

  // Data queries
  const { data: rawRecord, isLoading, isError, refetch } = usePipelineRecord(id ?? null);
  // Same list (and cache) as the page's FollowupPanel — for the "Complete this
  // lead" check below.
  const { data: recordFollowups, isFetched: followupsFetched } = useTimelineList('followups', 'deal', id ?? null, { anyParentType: true });
  const { data: scoreMap = {} } = useDealScores();
  const { data: users = [] } = useUserOptions();
  const salesUsers = useMemo(
    () => (Array.isArray(users) ? users : []).filter((u) =>
      u && (
        (u.department || '').toLowerCase() === 'sales'
        || (u.department || '').toLowerCase() === 'general_manager'
        || u.role === 'superadmin'
        || u.role === 'department_admin'
        || (u.username || '').toLowerCase().startsWith('bdm')
      )
    ),
    [users]
  );
  const { data: activity = [], isLoading: activityLoading, isError: activityError } = useEntityActivity('deal', id ?? null);
  const { data: leadDocs = [] } = useSalesDocs({ relatedToType: 'lead', relatedToId: id ?? null });
  const { data: dealDocs = [], isLoading: docsLoading } = useSalesDocs({ relatedToType: 'deal', relatedToId: id ?? null });
  // A record moves from the "lead" to the "deal" side of the pipeline over its
  // life, so a document filed under either legacy type may exist against the
  // same id — both are shown rather than only whichever type matches today.
  const documents = useMemo(() => [...leadDocs, ...dealDocs], [leadDocs, dealDocs]);
  const { createDoc } = useSalesDocMutations();
  const { data: stagesData } = usePipelineStages();

  // Mutations
  const { update, remove, moveToTrash, moveStage, decideReassignment } = usePipelineMutations();
  const { assignDeal, respondAssignment } = useDealMutations();

  // Permissions — mirrors the backend's requireEitherPermission('leads.X',
  // 'deals.X'): a merged record is a lead and a deal at different points in
  // its life, and the permission catalogue still has a separate ladder for
  // each, so requiring only one would lock out whoever holds the other.
  const canUpdate = useHasAnyPermission('leads.update', 'deals.update');
  const canDelete = useHasAnyPermission('leads.delete', 'deals.delete');
  const canCreateQuotation = useHasAnyPermission('leads.move_to_quotation', 'deals.move_to_quotation');
  const canAppointment = useHasAnyPermission('leads.appointments', 'deals.appointments');
  // Follow-ups on the record itself (client, 2026-10-05): admins and superadmins
  // could not see or log them here — only the create form captured one.
  const canFollowups = useHasAnyPermission('followups.read');

  // UI state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [trashConfirmOpen, setTrashConfirmOpen] = useState(false);
  const [lostReasonOpen, setLostReasonOpen] = useState(false);
  const [pendingRegressionStage, setPendingRegressionStage] = useState<string | null>(null);
  const [docFormOpen, setDocFormOpen] = useState(false);
  // An admin / manager completing someone else's lead from the banner.
  const [completeOpen, setCompleteOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [po, setPo] = useState<CustomerPoValue | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [assignNote, setAssignNote] = useState('');

  // Hydrate with the AI win-score, keyed by this record's id the same way it
  // always was — the score service has no notion of "lead" vs "deal" either.
  const record = useMemo<PipelineRecord | null>(() => {
    if (!rawRecord) return null;
    return {
      ...rawRecord,
      ai_score: (scoreMap && typeof scoreMap === 'object' ? (scoreMap as any)[rawRecord.id] : undefined),
    } as PipelineRecord & { ai_score?: number };
  }, [rawRecord, scoreMap]);

  const nameOf = useMemo(() => {
    const map = new Map(users.map((u) => [u.id, u.display_name]));
    return (uid?: string | null) => (uid ? map.get(uid) ?? '' : '');
  }, [users]);

  // Dynamic stages from Database Master — must run on every render (not
  // just once `record` has loaded), otherwise the hook count differs between
  // the loading render and the loaded render and React throws "Rendered
  // fewer hooks than expected" the moment the query resolves.
  const { data: dbStages = [] } = useDealStages();

  const currentStageIndex = useMemo(() => {
    if (!record) return 0;
    const idx = dbStages.findIndex((s) => s.key === record.stage);
    return idx >= 0 ? idx : 0;
  }, [dbStages, record?.stage]);

  const stageOptions = useMemo(() => dbStages.map((s) => ({ value: s.key, label: s.label })), [dbStages]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-muted">
        <Spinner />
      </div>
    );
  }

  if (isError || !record) {
    return (
      <div className="p-8 bg-muted min-h-screen">
        <div className="max-w-xl mx-auto">
          <EmptyState
            title="Record Not Found"
            description="The requested record could not be loaded or you may not have access."
            action={
              <Button onClick={() => navigate(ROUTES.pipeline)}>
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Pipeline
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  const assigneeId = getAssignedId(record.assigned_to as any);
  const status = (record.assignment_status ?? 'none') as AssignmentStatus;
  const hasHandoff = status !== 'none';
  const assignerName = nameOf(record.assigned_by) || userName(record.user_id);
  // A record parked for reassignment approval before approval was bypassed has
  // no assignee on file; it is its creator's to work, so show them.
  const creatorId = getAssignedId(record.user_id as any);
  const assigneeName = nameOf(assigneeId) || (record.assigned_to ? userName(record.assigned_to) : '')
    || (record.reassignmentRequest?.status === 'pending'
      ? userName(record.user_id) || nameOf(creatorId)
        || (creatorId && creatorId === currentUserId ? (user?.displayName || user?.username || 'You') : '')
      : '');
  const iAmAssignee = !!currentUserId && assigneeId === currentUserId;
  const canRespond = iAmAssignee && status === 'pending';
  const isPipelineAdmin = Boolean(user && PIPELINE_ADMIN_ROLES.has(user.role));
  const pendingReassignment = record.reassignmentRequest?.status === 'pending' ? record.reassignmentRequest : null;
  const reassignmentToName = pendingReassignment ? userName(pendingReassignment.toUserId) : '';
  // Approval is bypassed (contacts are shared): nothing is locked any more,
  // matching isPipelineRecordLockedForWrite in src/pipeline-visibility.js.
  const pendingReassignmentLocked = false;
  const isSuperAdmin = user?.role === 'superadmin';

  // `salesUsers` was computed above and then never used — this list was built
  // from the unfiltered `users`, so a record could be handed to someone outside
  // Sales who would never be able to see it.
  //
  // YOURSELF INCLUDED. The list used to exclude the current user, which meant
  // an unassigned record could be handed to somebody else but never simply
  // taken — a rep looking at their own untaken enquiry had no way to claim it
  // from here.
  const assigneeOptions = [
    { value: '', label: 'Select a representative…' },
    ...salesUsers.map((u) => ({
      value: u.id,
      label: u.id === currentUserId ? `${u.display_name} (you)` : u.display_name,
    })),
  ];

  const handleStageChange = (nextStage: string) => {
    if (nextStage === 'closed_lost') {
      setLostReasonOpen(true);
      return;
    }

    // closed_lost sits at the end of dbStages (highest index) and is handled
    // above, never treated as a "regression" — matches
    // validateManualStageChange in src/pipeline-policy.js.
    const fromIdx = dbStages.findIndex((s) => s.key === record.stage);
    const toIdx = dbStages.findIndex((s) => s.key === nextStage);
    if (fromIdx !== -1 && toIdx !== -1 && toIdx < fromIdx) {
      setPendingRegressionStage(nextStage);
      return;
    }

    moveStage.mutate(
      { id: record.id, stage: nextStage },
      {
        onSuccess: (res) => {
          toast.success(nextStage === 'closed_won' ? 'Record marked as WON!' : `Moved to ${nextStage.replace(/_/g, ' ')}.`);
          // The move succeeded but may have arrived somewhere a Sales Order
          // should already exist. Said once here, and again as a standing
          // notice below — a toast is gone in seconds and whoever needs to act
          // may not be the person who moved the card.
          for (const w of (res as { warnings?: string[] })?.warnings ?? []) toast(w, { icon: '⚠️', duration: 8000 });
          refetch();
        },
        onError: (err) => toast.error(stageErrorText(err, 'Failed to move the record.')),
      }
    );
  };

  const confirmLost = (payload: { lostReasonId: string; lostReasonNote: string }) => {
    moveStage.mutate(
      { id: record.id, stage: 'closed_lost', ...payload },
      {
        onSuccess: () => {
          setLostReasonOpen(false);
          toast.success('Marked as lost with reason recorded.');
          refetch();
        },
        onError: (err) => toast.error(stageErrorText(err, 'Failed to move the record.')),
      }
    );
  };

  const confirmRegression = (moveNote: string) => {
    if (!pendingRegressionStage) return;
    moveStage.mutate(
      { id: record.id, stage: pendingRegressionStage, moveNote },
      {
        onSuccess: () => {
          toast.success(`Moved back to ${pendingRegressionStage.replace(/_/g, ' ')}.`);
          setPendingRegressionStage(null);
          refetch();
        },
        onError: (err) => toast.error(stageErrorText(err, 'Failed to move the record.')),
      }
    );
  };

  const submitReassign = () => {
    if (!selectedAssignee) return;
    assignDeal.mutate(
      { id: record.id, assignee: selectedAssignee, note: assignNote.trim() },
      {
        onSuccess: () => {
          // Handing a lead over takes it off your own pipeline — no need to
          // close it (closing it would close it for your colleague too).
          toast.success('Handed over. It is off your pipeline now — it comes back only if they reject it.', { duration: 6000 });
          setReassignOpen(false);
          setSelectedAssignee('');
          setAssignNote('');
          refetch();
        },
        onError: () => toast.error('Failed to reassign the record.'),
      }
    );
  };

  const handleRespond = (action: 'accept' | 'reject') => {
    respondAssignment.mutate(
      { id: record.id, action },
      {
        onSuccess: () => {
          toast.success(action === 'accept' ? 'Accepted — this lead is yours now.' : 'Rejected — it goes back to whoever assigned it.');
          refetch();
        },
        onError: () => toast.error('Failed to update assignment.'),
      }
    );
  };

  const handleReassignmentDecision = (decision: 'approve' | 'reject') => {
    decideReassignment.mutate(
      { id: record.id, decision },
      {
        onSuccess: () => {
          toast.success(decision === 'approve' ? 'Reassignment approved.' : 'Reassignment rejected — record stays with its creator.');
          refetch();
        },
        onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to record the decision.'),
      }
    );
  };

  const handleDelete = () => {
    remove.mutate(record.id, {
      onSuccess: () => {
        toast.success('Record deleted.');
        navigate(ROUTES.pipeline);
      },
      onError: () => toast.error('Failed to delete the record.'),
    });
  };

  const handleMoveToTrash = () => {
    moveToTrash.mutate({ id: record.id }, {
      onSuccess: (result: any) => {
        toast.success('Record moved to Trash.');
        if (Array.isArray(result?.warnings) && result.warnings.length) {
          result.warnings.forEach((w: string) => toast(w));
        }
        navigate(ROUTES.pipeline);
      },
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to move the record to Trash.'),
    });
  };

  // The PO panel edits a local copy and saves on demand, rather than writing on
  // every keystroke — a half-typed PO number saved mid-edit would trip the
  // Order Confirmed gate for as long as it took to finish typing.
  const poValue = po ?? customerPoFromRecord(record as unknown as Record<string, unknown>);
  const savePo = () => {
    if (!record) return;
    update.mutate(
      { id: record.id, payload: { ...poValue, customerPoDate: poValue.customerPoDate || null } as never },
      {
        onSuccess: () => {
          toast.success('Purchase order details saved.');
          setPo(null);
          refetch();
        },
        onError: () => toast.error('Could not save the purchase order details.'),
      },
    );
  };

  const handleLinesChange = (lines: PipelineLine[]) => {
    update.mutate(
      { id: record.id, payload: { lines } },
      {
        onSuccess: () => {
          toast.success('Products updated.');
          refetch();
        },
        onError: () => toast.error('Failed to update products.'),
      }
    );
  };


  // The linked Contact as it is NOW (the API sends it with the record) — the
  // record's own name/phone/email are a snapshot from when it was created, so
  // an edit made on the contact afterwards must win here.
  const liveContact = (record as unknown as { contact?: { name?: string; phone?: string; email?: string } | null })?.contact || null;
  const contactPhone = liveContact?.phone || record?.phone || '';
  const contactEmail = liveContact?.email || record?.email || '';

  // ── QUOTED RECORDS ARE READ-ONLY ──
  // Once a quotation has been ISSUED against this record, the record stops
  // being a live document and becomes the enquiry it started as: what was
  // asked for, and what was offered in answer. Editing it afterwards makes it
  // disagree with the paper the customer is holding, and nothing downstream
  // re-reads it — the quotation carries its own snapshot of the lines, the
  // customer and the price. A changed requirement is a REVISED quotation
  // (Revise, on the quotation itself), which keeps both versions.
  //
  // A DRAFT quotation does not freeze anything: it is unnumbered, unsent and
  // discardable. The server decides this — GET /api/pipeline/:id — and refuses
  // the write regardless, so this is the courtesy, not the enforcement.
  //
  // What stays live: the stage, follow-ups, appointments, the activity trail
  // and the sampling panel. Those are the CRM's actual work and none of them
  // restate what the quotation says.
  const quoted = record.hasIssuedQuotation === true;
  const canEditRecord = canUpdate && !quoted && !pendingReassignmentLocked;

  const side = stagesData?.stages.find((s) => s.key === record.stage)?.side;

  // Product + next follow-up are mandatory on every open record (client,
  // 2026-10-28). Asked of the person who handles it — not of a manager
  // reading someone else's record, nor before an assignment is accepted.
  const handlerId = assigneeId || (!record.assigned_to ? creatorId : '');
  const iHandleIt = !!currentUserId && handlerId === currentUserId;
  const leadGaps = followupsFetched ? leadWorkGaps(record, recordFollowups ?? []) : [];
  const openFollowupId = (recordFollowups ?? []).find((f) => f.status === 'open')?._id ?? null;
  // The person spoken to also fills the record's own name / phone when those
  // are empty — never overwriting what the enquiry already carried.
  const fillRecordContact = ({ name, phone }: { name: string; phone: string }) => {
    const payload: Partial<typeof record> = {};
    if (!String(record.contact_name || '').trim() && name) payload.contact_name = name;
    if (!String(record.phone || '').trim() && phone) payload.phone = phone;
    if (Object.keys(payload).length) update.mutate({ id: record.id, payload }, { onSuccess: () => refetch() });
  };
  // Required for EVERYONE who opens it and can edit it (client, 2026-10-29)
  // — the assignee of a lead still awaiting their acceptance accepts first.
  const mustComplete = canUpdate && !(iHandleIt && status === 'pending') && leadGaps.length > 0;
  // Everyone else who opens an incomplete lead sees what is missing and who
  // owes it, and may complete it themselves (not forced).
  const showIncompleteBanner = !mustComplete && leadGaps.length > 0;
  const GAP_LABEL: Record<string, string> = { product: 'product', contact: 'person spoken to', followup: 'next follow-up' };
  const handlerName = assigneeName || userName(record.user_id) || '';
  // "At or past Order Confirmed" — the point from which Dispatch is expected
  // to be able to see the business.
  const orderConfirmedIndex = dbStages.findIndex((st) => st.key === 'order_confirmed');

  return (
    <div className="space-y-6">
      {/* ── TOP BREADCRUMB & ACTION BAR ── */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(ROUTES.pipeline)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card text-on-surface-variant hover:bg-muted hover:text-on-surface shadow-xs transition-all"
            title="Back to Pipeline"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-info/10 px-2 py-0.5 font-mono text-data-xs font-bold text-info-text border border-info/30">
                {leadNumberLabel(record.lead_no) ? `LEAD ${leadNumberLabel(record.lead_no)}` : `RECORD #${record.id}`}
              </span>
              <span className="text-slate-300">•</span>
              <span className="text-xs font-semibold text-on-surface-variant capitalize">
                {DEAL_SOURCE_LABELS[record.source || 'direct'] || record.source || 'Direct'}
              </span>
            </div>
            {/* The COMPANY, not the person. This read `record.title ||
                record.name`, and `name` holds the contact person's name on
                every record created through the old form — so a B2B account
                was headed by whichever individual happened to answer the
                phone. `name` remains the fallback for records that only ever
                had one. */}
            <h1 className="mt-0.5 text-2xl font-bold text-on-surface tracking-tight">
              {record.business_name || record.title || record.name}
            </h1>
            {(record.existing_customer || record.duplicate_match === 'name') && (
              <p className={record.existing_customer
                ? 'mt-1 inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-danger/30 bg-danger/10 px-2.5 py-1 text-xs font-semibold text-danger-text'
                : 'mt-1 inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-warning/30 bg-warning/10 px-2.5 py-1 text-xs font-semibold text-warning-text'}>
                {record.existing_customer
                  ? 'Already in system — priority: a new enquiry from a customer we know (same phone or email).'
                  : 'Same name — check: a client with this name is already in the system, with different contact details.'}
                {record.related_owner_name && <span className="font-normal">Earlier record with {record.related_owner_name}{record.related_stage ? ` (${record.related_stage.replace(/_/g, ' ')})` : ''}.</span>}
                {record.related_record_id && (
                  <button type="button" className="font-semibold underline" onClick={() => navigate(`/pipeline/${record.related_record_id}`)}>Open earlier record</button>
                )}
              </p>
            )}
            {(record.contact_name || record.name) && record.business_name && (
              <p className="text-xs font-semibold text-on-surface-variant">
                {record.contact_name || record.name}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2.5">
          {canCreateQuotation && !quoted && !pendingReassignmentLocked && (
            <button
              type="button"
              // Nothing is raised for an incomplete lead (server: LEAD_INCOMPLETE).
              disabled={leadGaps.length > 0}
              title={leadGaps.length > 0 ? 'Complete this lead first — product, person spoken to and next follow-up.' : undefined}
              onClick={() => navigate(`${ROUTES.quotationNew}?dealId=${encodeURIComponent(record.id)}`)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-3.5 py-2 text-xs font-bold text-on-surface-variant shadow-xs hover:bg-muted transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileSpreadsheet className="h-4 w-4 text-info-text" />
              Create Quotation
            </button>
          )}

          {/* No invoice from the pipeline record: a tax invoice comes from an
              accepted quotation, so every invoice has a quote behind it. */}

          {canEditRecord && (
            <button
              type="button"
              onClick={() => navigate(`/pipeline/${record.id}/edit`)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-white shadow-md  hover:bg-primary-dim transition-all"
            >
              <Edit className="h-3.5 w-3.5" />
              Edit Record
            </button>
          )}

          {canDelete && !quoted && (
            <button
              type="button"
              onClick={() => setDeleteConfirmOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-danger/30 bg-danger/10 px-3.5 py-2 text-xs font-bold text-danger-text hover:bg-danger/10 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          )}

          {/* Move to Trash — the safe, reversible option, so it stays available
              even once a quotation exists (unlike Delete above, which the
              server refuses at that point). */}
          {canDelete && (
            <button
              type="button"
              onClick={() => setTrashConfirmOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-3.5 py-2 text-xs font-bold text-on-surface-variant shadow-xs hover:bg-muted transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Move to Trash
            </button>
          )}
        </div>
      </div>

      <SharedRecordBanner sharing={record.sharing} currentUserId={currentUserId} />

      {showIncompleteBanner && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3">
          <div className="min-w-0 text-body-sm text-warning-text">
            <p className="font-semibold">
              Incomplete lead — missing {leadGaps.map((g) => GAP_LABEL[g]).join(', ')}.
            </p>
            <p>
              {status === 'pending'
                ? `${handlerName || 'The assignee'} must accept the assignment first; then they have to complete it before working on the lead.`
                : `${handlerName || 'The assigned salesperson'} has to complete it before working on the lead.`}
            </p>
          </div>
          {canUpdate && (
            <Button size="sm" onClick={() => setCompleteOpen(true)}>Complete now</Button>
          )}
        </div>
      )}

      {/* ── WHY THIS RECORD IS READ-ONLY ──
          Not an error and not a permission problem, so it is stated plainly
          and points at the document that caused it: the next change belongs on
          the quotation, through Revise. */}
      {quoted && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-border bg-muted/70 p-4">
          <div className="flex items-start gap-2.5">
            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-on-surface-variant" />
            <div>
              <p className="text-sm font-bold text-on-surface">
                Enquiry record — read-only
              </p>
              <p className="text-xs text-on-surface-variant">
                {record.issuedQuotationNumber || 'A quotation'} has been issued against this record, so it is
                kept as the enquiry it was. To change what is being offered, revise the quotation.
              </p>
            </div>
          </div>
          {record.issuedQuotationId && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/quotations/${record.issuedQuotationId}`)}>
              Open {record.issuedQuotationNumber || 'quotation'}
            </Button>
          )}
        </div>
      )}

      {/* ── CLIENT & ACCOUNT DETAILS — first thing after the header, so who
          this record is for is answered before what they might buy. ── */}
      <div className="rounded-card border border-border bg-card p-5 shadow-xs">
        <h3 className="text-sm font-bold text-on-surface flex items-center gap-2 border-b border-border pb-3">
          <Building2 className="h-4 w-4 text-info-text" />
          Customer & Account Context
        </h3>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
          <div className="rounded-xl bg-muted p-3">
            <span className="text-data-xs font-bold uppercase text-on-surface-subtle">Contact Person</span>
            {/* Falls back to `name`: the old create form wrote the person's
                name there and never touched contact_name, so this card read
                "—" on every record it made. */}
            <p className="mt-1 text-sm font-bold text-on-surface">
              {liveContact?.name || record.contact_name || (record.business_name ? record.name : '') || '—'}
            </p>
          </div>
          <div className="rounded-xl bg-muted p-3">
            <span className="text-data-xs font-bold uppercase text-on-surface-subtle">Business / Company</span>
            <p className="mt-1 text-sm font-bold text-on-surface">{record.business_name || '—'}</p>
          </div>
          <div className="rounded-xl bg-muted p-3">
            <span className="text-data-xs font-bold uppercase text-on-surface-subtle">Phone</span>
            {contactPhone ? (
              <a href={`tel:${contactPhone}`} className="mt-1 block text-sm font-bold text-info-text hover:underline">
                {contactPhone}
              </a>
            ) : (
              <p className="mt-1 text-sm font-bold text-on-surface-variant">—</p>
            )}
          </div>
          <div className="rounded-xl bg-muted p-3">
            <span className="text-data-xs font-bold uppercase text-on-surface-subtle">Email</span>
            {contactEmail ? (
              <a href={`mailto:${contactEmail}`} className="mt-1 block text-sm font-bold text-info-text hover:underline truncate">
                {contactEmail}
              </a>
            ) : (
              <p className="mt-1 text-sm font-bold text-on-surface-variant">—</p>
            )}
          </div>
        </div>

        {record.service_required && (
          <div className="mt-4 rounded-xl bg-info/10/60 border border-info/30 p-3 text-xs">
            <span className="font-bold text-info-text">Service Required / Requirement:</span>
            <p className="mt-0.5 text-on-surface">{record.service_required}</p>
          </div>
        )}
      </div>

      {/* ── DISPATCH CANNOT SEE THIS ──
          A stage no longer creates the Sales Order (the order is the
          commercial act; the stage is a marker that follows it), so a record
          can sit at Order Confirmed or beyond with nothing behind it. The
          stage move says so once in a toast; this stays until it is resolved,
          because whoever has to act may not be whoever moved the card. */}
      {record.hasSalesOrder === false && dbStages.findIndex((st) => st.key === record.stage) >= orderConfirmedIndex
        && orderConfirmedIndex >= 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-warning/30 bg-warning/10 p-4">
          <div className="flex items-start gap-2.5">
            <Truck className="mt-0.5 h-4 w-4 shrink-0 text-warning-text" />
            <div>
              <p className="text-sm font-bold text-warning-text">No Sales Order for this record</p>
              <p className="text-xs text-warning-text">
                It has reached {(dbStages.find((st) => st.key === record.stage)?.label) || record.stage.replace(/_/g, ' ')},
                but Dispatch has nothing to pick up. Raise one by converting the accepted quotation.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate(ROUTES.quotations)}>
            Go to Quotations
          </Button>
        </div>
      )}

      <CustomerHistoryPanel recordId={record.id} />

      {/* ── CUSTOMER PURCHASE ORDER ──
          Sits next to the client block because it answers the same question:
          who is this for, and on what authority. Only blocks Order Confirmed
          when the record has declared it is PO-backed — see CustomerPoFields. */}
      {/* NOT gated on `quoted`: the PO is the customer's answer to the
          quotation, so it is recorded after one is issued (the server exempts
          the PO fields from the quoted-record lock for the same reason). */}
      {canUpdate && !pendingReassignmentLocked && (
        <div className="space-y-3">
          <CustomerPoFields value={poValue} onChange={setPo} />
          {po && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setPo(null)} disabled={update.isPending}>
                Discard
              </Button>
              <Button onClick={savePo} disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save PO Details'}
              </Button>
            </div>
          )}
          {!po && isCustomerPoIncomplete(poValue) && (
            <p className="rounded-xl border border-warning/30 bg-warning/10 px-3.5 py-2.5 text-xs font-semibold text-warning-text">
              This record is marked as PO-backed but has no PO number, so it cannot move to Order Confirmed yet.
            </p>
          )}
        </div>
      )}

      {/* ── 5 KEY METRIC HIGHLIGHT CARDS ── */}
      <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 xl:grid-cols-5">
        {/* Value */}
        <div className="rounded-card border border-border bg-card p-4 shadow-xs">
          <span className="text-data-xs font-bold uppercase tracking-wider text-on-surface-subtle">Record Value</span>
          <p className="mt-1.5 text-2xl font-bold tabular-nums text-on-surface leading-tight">
            {formatMoney(record.value, record.currency)}
          </p>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">{record.currency || 'INR'} Commercial</p>
        </div>

        {/* Probability & AI */}
        <div className="rounded-2xl border border-info/30 bg-info/10/40 p-4 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-data-xs font-bold uppercase tracking-wider text-info-text">Win Probability</span>
            {typeof (record as any).ai_score === 'number' && (
              <span className="flex items-center gap-0.5 rounded-full bg-primary text-white px-2 py-0.5 text-data-xs font-bold">
                <Zap className="h-2.5 w-2.5" /> AI {(record as any).ai_score}%
              </span>
            )}
          </div>
          <p className="mt-1.5 text-2xl font-bold tabular-nums text-info-text leading-tight">
            {record.probability != null ? `${record.probability}%` : `${(record as any).ai_score ?? 20}%`}
          </p>
          <p className="mt-1 text-xs text-info-text font-medium">Stage-weighted model</p>
        </div>

        {/* Priority */}
        <div className="rounded-2xl border border-warning/30 bg-warning/10/40 p-4 shadow-xs">
          <span className="text-data-xs font-bold uppercase tracking-wider text-warning-text">Priority Level</span>
          <p className="mt-1.5 text-2xl font-bold capitalize text-warning-text leading-tight">
            {record.priority || 'Medium'}
          </p>
          <p className="mt-1 text-xs text-warning-text font-medium">Sales SLA Focus</p>
        </div>

        {/* Expected Close */}
        <div className="rounded-2xl border border-teal-100 bg-teal-50/40 p-4 shadow-xs">
          <span className="text-data-xs font-bold uppercase tracking-wider text-teal-600">Expected Close</span>
          <p className="mt-1.5 text-xl font-bold tabular-nums text-teal-800 leading-tight">
            {record.expected_close || 'Not Scheduled'}
          </p>
          <p className="mt-1 text-xs text-teal-600 font-medium">Target Closing Timeline</p>
        </div>

        {/* Owner */}
        <div className="rounded-2xl border border-primary/30 bg-primary-container/40 p-4 shadow-xs col-span-2 sm:col-span-1">
          <span className="text-data-xs font-bold uppercase tracking-wider text-primary">Record Owner</span>
          <p className="mt-1.5 text-lg font-bold text-primary truncate leading-tight">
            {assigneeName || 'Unassigned'}
          </p>
          <p className="mt-1 text-xs text-primary font-medium">Created by {assignerName || 'System'}</p>
        </div>
      </div>

      {/* ── DYNAMIC DATABASE PIPELINE PROGRESSION (NON-CLICKABLE VISUAL TRACKER) ── */}
      <div className="rounded-card border border-border bg-card p-5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface-subtle">
              Pipeline Stage Progression
            </span>
            <p className="text-xs text-on-surface-variant mt-0.5">
              Live database stages. Current Stage: <strong className="text-info-text font-bold capitalize">{dbStages.find((s) => s.key === record.stage)?.label || record.stage?.replace(/_/g, ' ')}</strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-on-surface-variant bg-muted px-2.5 py-1 rounded-md font-medium border border-border">
              Stages advance automatically
            </span>
            {canUpdate && !pendingReassignmentLocked && record.stage !== 'closed_lost' && record.stage !== 'closed_won' && (
              <button
                type="button"
                onClick={() => handleStageChange('closed_lost')}
                className="inline-flex items-center gap-1 rounded-lg border border-danger/30 bg-danger/10 px-2.5 py-1 text-xs font-bold text-danger-text hover:bg-danger/20 transition shadow-xs"
                title="Mark this lead as lost, irrelevant, or not needed"
              >
                <XCircle className="h-3.5 w-3.5" />
                Close as Lost / Irrelevant
              </button>
            )}
          </div>
        </div>

        {/* Visual Stage Stepper Track (Non-Clickable, Linked to Database Stages) */}
        <div className="flex items-center gap-2 overflow-x-auto pb-3 pt-1">
          {dbStages.map((s, idx) => {
            const isCurrent = idx === currentStageIndex;
            const isCompleted = idx < currentStageIndex;

            return (
              <div
                key={s.key}
                className={`flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-bold shrink-0 select-none transition-all ${
                  isCurrent
                    ? 'bg-primary text-white shadow-md  ring-2 ring-primary/30'
                    : isCompleted
                    ? 'bg-success/10 text-success-text border border-success/30'
                    : 'bg-muted text-on-surface-subtle border border-border'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full text-data-xs font-bold ${
                    isCurrent
                      ? 'bg-card text-info-text'
                      : isCompleted
                      ? 'bg-success text-white'
                      : 'bg-slate-200 text-on-surface-variant'
                  }`}
                >
                  {isCompleted ? '✓' : idx + 1}
                </span>
                <span className="truncate">{s.label}</span>
                {isCurrent && (
                  <span className="h-1.5 w-1.5 rounded-full bg-card animate-pulse ml-0.5" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── QUALIFICATION ── */}

      {/* ── PRODUCTS ── */}
      <div className="rounded-card border border-border bg-card p-5 shadow-xs">
        <h3 className="text-sm font-bold text-on-surface flex items-center gap-2 border-b border-border pb-3 mb-4">
          <Package className="h-4 w-4 text-info-text" />
          Products
        </h3>
        <ProductLinesEditor lines={record.lines ?? []} onChange={handleLinesChange} readOnly={!canEditRecord} currency={record.currency || 'INR'} />
        {(record.lines?.length ?? 0) > 0 && (
          <div className="mt-3 flex justify-end text-sm font-bold text-on-surface">
            Grand total: {formatMoney(record.grandTotal, record.currency)}
          </div>
        )}
      </div>

      {/* ── SAMPLES ── */}
      {/* Full read-write: request a sample, dispatch it, record feedback, and
          convert it, all without leaving the record. Dispatching, converting,
          or logging a negative verdict on the sample moves this record's
          stage automatically (Sample/Trial, then Trial Successful — or Trial
          Unsuccessful for a rejected/no-response verdict) — see
          DOCUMENT_EVENTS.SAMPLE_DISPATCHED / SAMPLE_CONVERTED /
          SAMPLE_REJECTED in pipeline-policy.js. A sample still in draft has
          not gone anywhere yet, so it does not move the stage on its own,
          the same "a draft is not evidence" rule a parked quotation follows. */}
      <LeadSamplingPanel leadId={record._id} canMutate={canUpdate} />

      {/* ── MAIN 2-COLUMN DETAIL LAYOUT ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr,0.9fr]">
        {/* LEFT COLUMN: ASSIGNMENT, NOTES, DOCUMENTS — client details now sit
            at the top of the page, before Qualification/Products/Samples. */}
        <div className="space-y-6">
          {/* Assignment & Handoff Card */}
          <div className="rounded-card border border-border bg-card p-5 shadow-xs">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h3 className="text-sm font-bold text-on-surface flex items-center gap-2">
                <User className="h-4 w-4 text-primary" />
                Ownership & Assignment Handoff
              </h3>
              {hasHandoff && ASSIGNMENT_META[status as Exclude<AssignmentStatus, 'none'>] && (
                <Badge tone={ASSIGNMENT_META[status as Exclude<AssignmentStatus, 'none'>]?.tone ?? 'neutral'}>
                  {ASSIGNMENT_META[status as Exclude<AssignmentStatus, 'none'>]?.label ?? status}
                </Badge>
              )}
            </div>

            <div className="mt-3 text-sm text-on-surface">
              {assigneeName ? (
                <span>
                  Currently assigned to <strong className="font-bold text-on-surface">{assigneeName}</strong>
                </span>
              ) : (
                <span className="text-on-surface-subtle font-medium">Unassigned</span>
              )}
            </div>

            {/* Cross-BDM contact-ownership reassignment — a distinct concept
                from the accept/reject handoff below (that's a peer accepting
                a hand-off someone else decided; this is an admin deciding
                whether the record moves at all, because its linked Contact
                already belongs to a different BDM). Unassigned above IS
                expected while this is pending — nobody legitimately owns it
                yet. */}
            {/* Tracking only — approval is bypassed, so the record already works
                normally for whoever raised it. The Super Admin can still hand it
                to the contact's earlier holder, or close the old request. */}
            {pendingReassignment && isSuperAdmin && (
              <div className="mt-3 rounded-xl border border-border bg-muted p-3.5">
                <p className="text-sm font-bold text-on-surface">Old reassignment request (tracking)</p>
                <p className="mt-1 text-xs text-on-surface-variant">
                  Raised on a contact first added by <strong>{reassignmentToName || 'another BDM'}</strong>. It stays with
                  whoever raised it unless you hand it over.
                </p>
                {isPipelineAdmin && (
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleReassignmentDecision('approve')}
                      disabled={decideReassignment.isPending}
                      className="bg-success/10 hover:bg-success/10"
                    >
                      <Check className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReassignmentDecision('reject')}
                      disabled={decideReassignment.isPending}
                      className="text-danger-text border-danger/30 hover:bg-danger/10"
                    >
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            )}

            {record.assignment_note && (
              <p className="mt-2 text-xs italic text-on-surface-variant bg-muted p-2.5 rounded-xl border border-border">
                "{record.assignment_note}"
              </p>
            )}

            {canRespond && (
              <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={() => handleRespond('accept')} className="bg-success/10 hover:bg-success/10">
                  <Check className="h-4 w-4 mr-1" /> Accept Handoff
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleRespond('reject')} className="text-danger-text border-danger/30 hover:bg-danger/10">
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
              </div>
            )}

            {!reassignOpen && !pendingReassignmentLocked && (
              <button
                type="button"
                onClick={() => setReassignOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-info-text hover:underline"
              >
                <UserPlus className="h-3.5 w-3.5" />
                {assigneeName ? 'Reassign…' : 'Assign to a Representative…'}
              </button>
            )}

            {reassignOpen && (
              <div className="mt-4 flex flex-col gap-3 rounded-xl border border-border bg-muted p-3.5">
                <Select
                  name="record-assignee"
                  label="Select Representative"
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                  options={assigneeOptions}
                />
                <textarea
                  rows={2}
                  placeholder="Optional hand-off note or context…"
                  value={assignNote}
                  onChange={(e) => setAssignNote(e.target.value)}
                  className="w-full rounded-xl border border-border bg-card px-3 py-2 text-xs text-on-surface focus:outline-none focus:ring-2 focus-visible:ring-ring/20"
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setReassignOpen(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" disabled={!selectedAssignee} onClick={submitReassign} className="bg-info/10 hover:bg-info/10">
                    <ArrowRight className="h-3.5 w-3.5 mr-1" /> Assign Now
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Notes & Requirements */}
          {record.notes && (
            <div className="rounded-card border border-border bg-card p-5 shadow-xs">
              <h3 className="text-sm font-bold text-on-surface flex items-center gap-2 border-b border-border pb-3">
                <FileText className="h-4 w-4 text-on-surface-variant" />
                Notes & Intelligence
              </h3>
              <p className="mt-3 whitespace-pre-wrap text-xs leading-relaxed text-on-surface-variant">{record.notes}</p>
            </div>
          )}

          {/* Additional Info Fields */}
          <AdditionalInfoDisplay
            entity={side === 'lead' ? 'lead' : 'deal'}
            values={(record.additionalInfoValues || []).map((v) => ({ ...v, fieldId: v.fieldId || '' }))}
          />

          {/* Attached Documents */}
          <div className="rounded-card border border-border bg-card p-5 shadow-xs">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h3 className="text-sm font-bold text-on-surface flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-teal-600" />
                Attached Commercial Documents
              </h3>
              <button
                type="button"
                onClick={() => setDocFormOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-bold text-info-text hover:underline"
              >
                <Plus className="h-3.5 w-3.5" /> Attach Document
              </button>
            </div>

            {docsLoading ? (
              <div className="flex h-24 items-center justify-center">
                <Spinner />
              </div>
            ) : documents.length === 0 ? (
              <div className="mt-3 rounded-xl border border-dashed border-border p-6 text-center text-xs text-on-surface-subtle">
                No documents or quotations attached to this record yet.
              </div>
            ) : (
              <div className="mt-3 space-y-2.5">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between rounded-xl border border-border bg-muted/70 p-3 hover:bg-muted/70 transition-colors"
                  >
                    <div>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-bold text-info-text hover:underline flex items-center gap-1"
                      >
                        {doc.title} <ExternalLink className="h-3 w-3" />
                      </a>
                      <div className="mt-0.5 flex items-center gap-2 text-data-xs text-on-surface-variant">
                        <span className="capitalize">{doc.type?.replace('_', ' ') || 'Document'}</span>
                        <span>•</span>
                        <span className="capitalize font-semibold text-on-surface-variant">{doc.status || 'Draft'}</span>
                        <span>•</span>
                        <span>{doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-IN') : ''}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: FOLLOW-UPS, APPOINTMENTS, ACTIVITY */}
        <div className="space-y-6">
          {/* Follow-ups — every one logged on this record, whichever side (lead /
              deal) it was at; a new one is filed under the record's current side,
              the same way the stage-move follow-up is. */}
          {canFollowups && (
            <div className="rounded-card border border-border bg-card p-5 shadow-xs">
              <FollowupPanel parentType={side === 'lead' ? 'lead' : 'deal'} parentId={record.id} anyParentType />
            </div>
          )}

          {/* Appointments Panel */}
          {canAppointment && (
            <div className="rounded-card border border-border bg-card p-5 shadow-xs">
              <AppointmentPanel parentType={timelineParent} parentId={record.id} />
            </div>
          )}

          {/* Activity Timeline Stream */}
          <div className="rounded-card border border-border bg-card p-5 shadow-xs">
            <h3 className="text-sm font-bold text-on-surface flex items-center gap-2 border-b border-border pb-3">
              <Clock className="h-4 w-4 text-primary" />
              Activity Stream & Stage Log
            </h3>
            <div className="mt-3">
              <ActivityTimeline
                items={activity}
                isLoading={activityLoading}
                isError={activityError}
                showActor
                emptyTitle="No activity yet"
                emptyDescription="Stage changes, comments, and interactions will be recorded here."
              />
            </div>
          </div>
        </div>
      </div>

      {showIncompleteBanner && completeOpen && (
        <CompleteLeadDialog
          record={record}
          parentType={side === 'lead' ? 'lead' : 'deal'}
          gaps={leadGaps}
          openFollowupId={openFollowupId}
          onLinesChange={handleLinesChange}
          onContactSaved={fillRecordContact}
          required={false}
          onClose={() => setCompleteOpen(false)}
        />
      )}

      {mustComplete && (
        <CompleteLeadDialog
          record={record}
          parentType={side === 'lead' ? 'lead' : 'deal'}
          gaps={leadGaps}
          openFollowupId={openFollowupId}
          onLinesChange={handleLinesChange}
          onContactSaved={fillRecordContact}
        />
      )}

      <SalesDocFormModal
        open={docFormOpen}
        onClose={() => setDocFormOpen(false)}
        doc={{ relatedToType: side === 'lead' ? 'lead' : 'deal', relatedToId: record.id } as any}
        onCreate={(payload) =>
          createDoc.mutate(payload, {
            onSuccess: () => {
              setDocFormOpen(false);
              toast.success('Document attached.');
            },
          })
        }
        onUpdate={() => {}}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Record"
        message={`Are you sure you want to delete "${record.title || record.name}"? This cannot be undone.`}
        confirmLabel="Delete Record"
        danger
        loading={remove.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      <ConfirmDialog
        open={trashConfirmOpen}
        title="Move to Trash"
        message={`Move "${record.title || record.name}" to Trash? This also hides its client profile (if no other active `
          + 'pipeline record still needs it) and every quotation, proforma, sample and delivery challan raised against it '
          + '— only a Super Admin will be able to see or restore them from the Trash folder. Already-issued tax invoices '
          + 'are never affected.'}
        confirmLabel="Move to Trash"
        loading={moveToTrash.isPending}
        onConfirm={() => { handleMoveToTrash(); setTrashConfirmOpen(false); }}
        onCancel={() => setTrashConfirmOpen(false)}
      />

      {lostReasonOpen && (
        <LostReasonModal
          deal={asDeal(record)}
          isSaving={moveStage.isPending}
          onCancel={() => setLostReasonOpen(false)}
          onConfirm={confirmLost}
        />
      )}

      {pendingRegressionStage && (
        <StageRegressionNoteModal
          recordName={record.name || record.title || 'this record'}
          fromLabel={dbStages.find((s) => s.key === record.stage)?.label || record.stage}
          toLabel={dbStages.find((s) => s.key === pendingRegressionStage)?.label || pendingRegressionStage}
          isSaving={moveStage.isPending}
          onCancel={() => setPendingRegressionStage(null)}
          onConfirm={confirmRegression}
        />
      )}
    </div>
  );
}
